import dayjs from 'dayjs';
import { Spin } from 'antd';
import React, { useMemo } from 'react';
import LeaseV9NewDataTable from './LeaseV9NewDataTable';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import ViewEditModalHeader from 'components/modal/ViewEditModalHeader';

function formatNumber(number, decimals = 2) {
	if (typeof number !== 'number' || isNaN(number)) {
		return '';
	}

	const roundedNumber = Number(number.toFixed(decimals));

	const formatter = new Intl.NumberFormat('en-US', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});

	return formatter.format(roundedNumber);
}

const LeaseV9JournalModal = ({ showModal, setShowModal, checkResponseData = [], tableUpperLeftExtraInfo = null, isDataLoading }) => {
	const { processData, showGlData } = useMemo(() => {
		let arrayExcludeData = [];
		let hasGlData = false;

		const filteredData = checkResponseData?.filter((item) => item?.group !== 'Payment') || [];

		filteredData.forEach((item) => {
			if (item?.glData) {
				hasGlData = true;
			}

			if (item?.ccList?.length > 0) {
				item.ccList.forEach((ccItem) => {
					let itemData = {
						date: item?.entryDate?.dateString ? dayjs(item.entryDate.dateString).format('YYYY-MM-DD') : '',
						head: item?.head || '',
						description: item?.description || '',
						debit: '',
						credit: '',
						costCenterCode: ccItem.headCode || '',
						costCenterDescription: ccItem.head || '',
						headCode: item?.headCode || '',
						glAccount: item?.glData?.generalLedgerCode || '',
						glAccountDescription: item?.glData?.generalLegerDescription || '',
						headGroup: item?.headGroup || '',
					};

					if (ccItem?.debit && !isNaN(ccItem.debit)) {
						itemData.debit = formatNumber(ccItem.debit);
					}
					if (ccItem?.credit && !isNaN(ccItem.credit)) {
						itemData.credit = formatNumber(ccItem.credit);
					}
					arrayExcludeData.push(itemData);
				});
			} else {
				let itemData = {
					date: item?.entryDate?.dateString ? dayjs(item.entryDate.dateString).format('YYYY-MM-DD') : '',
					head: item?.head || '',
					description: item?.description || '',
					debit: '',
					credit: '',
					costCenterCode: '',
					costCenterDescription: '',
					headCode: item?.headCode || '',
					glAccount: item?.glData?.generalLedgerCode || '',
					glAccountDescription: item?.glData?.generalLegerDescription || '',
					headGroup: item?.headGroup || '',
				};

				if (item?.debit && !isNaN(item.debit)) {
					itemData.debit = formatNumber(item.debit);
				}
				if (item?.credit && !isNaN(item.credit)) {
					itemData.credit = formatNumber(item.credit);
				}
				arrayExcludeData.push(itemData);
			}
		});

		return { processData: arrayExcludeData, showGlData: hasGlData };
	}, [checkResponseData]);

	const checkColumnDesign = useMemo(
		() => [
			{
				id: 'date',
				width: '5%',
				Header: 'Date',
				align: 'center',
				cellAlign: 'center',
				Cell: (cell) => (
					<Typography align="center">
						{cell?.row?.original?.date
							? dayjs(cell?.row?.original?.date).format('YYYY-MM-DD')
							: cell?.row?.original?.detailsB
								? cell?.row?.original?.detailsB
								: ''}
					</Typography>
				),
			},
			{
				width: '5%',
				align: 'center',
				cellAlign: 'left',
				id: 'costCenterCode',
				isVisible: showGlData,
				Header: 'Cost Center',
				accessor: 'costCenterCode',
			},
			{
				width: '5%',
				align: 'center',
				cellAlign: 'left',
				isVisible: showGlData,
				id: 'costCenterDescription',
				Header: 'Cost Center Description',
				accessor: 'costCenterDescription',
			},
			{
				width: '5%',
				align: 'center',
				id: 'glAccount',
				cellAlign: 'left',
				Header: 'GL Account',
				isVisible: showGlData,
				accessor: (data) => (data?.glAccount ? data?.glAccount : data?.headCode ? data?.headCode : ''),
			},
			{
				width: '5%',
				align: 'center',
				cellAlign: 'left',
				isVisible: showGlData,
				id: 'glAccountDescription',
				Header: 'GL Account Description',
				accessor: (data) => (data?.glAccountDescription ? data?.glAccountDescription : data?.headGroup ? data?.headGroup : ''),
			},
			{
				width: '5%',
				align: 'center',
				id: 'leaserHead',
				cellAlign: 'left',
				accessor: 'headGroup',
				Header: 'Lease Heads',
				isVisible: !showGlData,
			},
			{
				width: '5%',
				align: 'center',
				id: 'description',
				cellAlign: 'left',
				Header: 'Description',
				accessor: 'description',
			},
			{
				id: 'debit',
				width: '5%',
				align: 'center',
				Header: 'Debit',
				cellAlign: 'center',
				accessor: (data) => (data?.debit && +data?.debit !== 0 ? data?.debit : ''),
				Cell: (cell) => (
					<Typography align="right">
						{cell?.row?.original?.debit && +cell?.row?.original?.debit !== 0 ? cell?.row?.original?.debit : ''}
					</Typography>
				),
			},
			{
				id: 'credit',
				width: '5%',
				align: 'center',
				Header: 'Credit',
				cellAlign: 'center',
				accessor: (data) => (data?.credit && +data?.credit !== 0 ? data?.credit : ''),
				Cell: (cell) => (
					<Typography align="right">
						{cell?.row?.original?.credit && +cell?.row?.original?.credit !== 0 ? cell?.row?.original?.credit : ''}
					</Typography>
				),
			},
		],
		[showGlData]
	);

	const defaultVisibleColumn = useMemo(() => {
		return checkColumnDesign.filter((column) => column.isVisible === false).map((column) => column.id);
	}, [checkColumnDesign]);

	const handleClose = () => {
		setShowModal(false);
	};

	const rowIndexColorMapping = useMemo(() => {
		let newMapping = {};

		Array.isArray(checkResponseData) &&
			checkResponseData?.forEach((lease, index) => {
				if (lease.isDelta) {
					newMapping[index] = {
						rowColor: '#E6F0FF',
					};
				}
			});

		return newMapping;
	}, [checkResponseData]);

	const renderContent = () => {
		if (isDataLoading) {
			return (
				<Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
					<Spin size="large" tip="Loading..." />
				</Box>
			);
		}

		if (!processData || processData.length === 0) {
			return (
				<Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} flexDirection="column">
					<Typography variant="h6" color="textSecondary" gutterBottom>
						No Data Available
					</Typography>
					<Typography variant="body2" color="textSecondary">
						There are no journal entries to display.
					</Typography>
				</Box>
			);
		}

		return (
			<LeaseV9NewDataTable
				pagination={false}
				DATA={processData}
				firstColStrick={false}
				onClickHandler={() => {}}
				COLUMNS={checkColumnDesign}
				columnFilterSelector={true}
				pageSize={processData.length}
				defaultVisibleColumn={defaultVisibleColumn}
				rowIndexColorMapping={rowIndexColorMapping}
				tableUpperLeftExtraInfo={tableUpperLeftExtraInfo}
				xlsxFileName={`${tableUpperLeftExtraInfo?.leaseId}_date`}
			/>
		);
	};

	return (
		<React.Fragment>
			<Dialog
				fullWidth={true}
				maxWidth={'lg'}
				open={showModal}
				keepMounted
				onClose={handleClose}
				aria-labelledby="draggable-dialog-title"
				sx={{ '& .MuiPaper-root': { borderRadius: '8px' } }}
			>
				<ViewEditModalHeader
					headerTitle="Journal Entry"
					handleClose={handleClose}
					isScreenSize={false}
					icon={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
				/>

				<DialogContent sx={{ pb: { xs: 4, md: 5 } }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						{renderContent()}
					</Box>
				</DialogContent>
			</Dialog>
		</React.Fragment>
	);
};

export default LeaseV9JournalModal;
